import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Plug, RefreshCw, Unplug, ShieldAlert } from "lucide-react";
import { Card, PageHeader, Btn, StatusPill, Empty } from "../../components/dash/Kit";
import { api, errMessage } from "../../lib/api";

export default function Connections() {
  const [state, setState] = useState(null);
  const [catalog, setCatalog] = useState({ available: [], planned: [] });
  const [busy, setBusy] = useState("");

  const load = () =>
    Promise.all([api.get("/social/connections"), api.get("/social/platforms")])
      .then(([c, p]) => {
        setState(c.data);
        setCatalog(p.data);
      })
      .catch(() => setState(false));

  useEffect(() => {
    load();
  }, []);

  const act = async (label, fn) => {
    setBusy(label);
    try {
      await fn();
      await load();
    } catch (e) {
      toast.error(errMessage(e));
    } finally {
      setBusy("");
    }
  };

  if (state === null) return <Card testId="connections-loading">Loading your connections…</Card>;
  if (state === false) return <Card testId="connections-error">We couldn't load your connections.</Card>;

  const byPlatform = Object.fromEntries(
    state.connections.filter((c) => c.status !== "disconnected").map((c) => [c.platform, c])
  );
  const liveCount = Object.keys(byPlatform).length;

  return (
    <div className="space-y-6" data-testid="connections-page">
      <PageHeader
        title="Connect your socials"
        body="Link the accounts you want PostMint to publish to. We use each platform's own sign-in — we never ask for your passwords."
      />

      <Card testId="connections-simulated-notice" className="border-amber-200 bg-amber-50">
        <p className="flex items-center gap-2 font-display text-base font-semibold text-amber-900">
          <ShieldAlert className="h-4 w-4" aria-hidden="true" />
          Simulated connections
        </p>
        <p className="mt-2 text-sm text-amber-900/85">
          Publishing to Instagram, Facebook, LinkedIn and TikTok needs approved developer access from each platform.
          Until that's in place, connecting here creates a clearly-labelled simulated account so you can test the full
          approve → schedule → publish flow. No posts leave PostMint.
        </p>
      </Card>

      <Card testId="connections-permissions-card">
        <p className="font-display text-base font-semibold text-slate-900">Before you connect an account</p>
        <dl className="mt-3 space-y-2.5 text-sm text-slate-600">
          {[
            ["What we ask for", "Permission to read your basic profile and page/account name, and to publish content you have approved. On some platforms we also request read access to post statistics so your Insights are accurate."],
            ["Why we need it", "To show which account content will go to, to publish approved posts at the scheduled time, and to report back how they performed."],
            ["What PostMint can do", "Publish, schedule and read the performance of posts you approve. We never post without your approval, never read your private messages, and never change your account settings or followers."],
            ["Your approval stays required", "Content is generated as a draft. Nothing is scheduled or published until you approve it."],
            ["How to disconnect", "Use the Disconnect button on the account below. It takes effect immediately and any queued posts for that account stop."],
            ["What happens to your tokens", "When you disconnect, or when you delete your account, the stored access token is deleted from PostMint so publishing is no longer possible. You can also revoke access from the platform's own settings at any time."],
          ].map(([label, value]) => (
            <div key={label}>
              <dt className="text-xs font-semibold uppercase tracking-wide text-slate-400">{label}</dt>
              <dd className="mt-0.5">{value}</dd>
            </div>
          ))}
        </dl>
      </Card>

      <Card testId="connections-quota-card">
        <p className="text-sm text-slate-600">
          <span className="font-semibold text-slate-900">
            {liveCount} of {state.max_accounts}
          </span>{" "}
          accounts connected on your plan.
          {!state.can_autopublish && " Automatic publishing starts on the Starter plan."}
        </p>
      </Card>

      <div className="grid gap-4 md:grid-cols-2" data-testid="connections-list">
        {catalog.available.map((p) => {
          const conn = byPlatform[p.id];
          return (
            <Card key={p.id} testId={`connection-${p.id.toLowerCase().replace(/\s+/g, "-")}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <p className="font-display text-lg font-semibold text-slate-900">{p.label}</p>
                  <p className="mt-1 text-xs text-slate-500">Supports {p.supports.join(", ")}</p>
                </div>
                {conn ? <StatusPill status={conn.status} /> : <StatusPill status="disconnected" />}
              </div>

              {conn ? (
                <>
                  <dl className="mt-4 grid gap-2 text-sm sm:grid-cols-2">
                    <div>
                      <dt className="text-xs font-semibold uppercase tracking-wide text-slate-400">Account</dt>
                      <dd className="mt-0.5 text-slate-800">{conn.account_name}</dd>
                    </div>
                    <div>
                      <dt className="text-xs font-semibold uppercase tracking-wide text-slate-400">Last sync</dt>
                      <dd className="mt-0.5 text-slate-800">{new Date(conn.last_sync).toLocaleString()}</dd>
                    </div>
                  </dl>
                  <div className="mt-4 flex flex-wrap gap-2">
                    <Btn
                      variant="outline"
                      disabled={busy === `re-${p.id}`}
                      onClick={() => act(`re-${p.id}`, () => api.post(`/social/connections/${conn.id}/reconnect`))}
                      testId={`reconnect-${p.id.toLowerCase().replace(/\s+/g, "-")}`}
                    >
                      {busy === `re-${p.id}` ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                      Reconnect
                    </Btn>
                    <Btn
                      variant="danger"
                      disabled={busy === `dc-${p.id}`}
                      onClick={() => act(`dc-${p.id}`, () => api.delete(`/social/connections/${conn.id}`))}
                      testId={`disconnect-${p.id.toLowerCase().replace(/\s+/g, "-")}`}
                    >
                      <Unplug className="h-4 w-4" /> Disconnect
                    </Btn>
                  </div>
                </>
              ) : (
                <Btn
                  className="mt-4"
                  disabled={busy === `c-${p.id}`}
                  onClick={() => act(`c-${p.id}`, () => api.post("/social/connections", { platform: p.id }))}
                  testId={`connect-${p.id.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  {busy === `c-${p.id}` ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plug className="h-4 w-4" />}
                  Connect {p.label}
                </Btn>
              )}
            </Card>
          );
        })}
      </div>

      <Card testId="connections-planned">
        <p className="font-display text-base font-semibold text-slate-900">Coming later</p>
        <p className="mt-2 text-sm text-slate-600">{catalog.planned.join(" · ")}</p>
      </Card>

      {state.connections.length === 0 && (
        <Empty testId="connections-empty" title="Nothing connected yet" body="Connect an account above to start publishing." />
      )}
    </div>
  );
}
